import { useCallback, useMemo } from 'react';

import { useParams } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';
import { AxiosRequestHeaders } from 'axios';

import { EFeatureFlag, EQueryKeys } from '../constants/enums';
import useAxios from '../hooks/useAxios';

import {
  EApiRoutes,
  getApiRouteOrMock
} from './api.utils';
import { useUserDetails } from './useUserDetails';

interface UseFeatureFlagsOptions {
  publisherId?: string;
}

export default function useFeatureFlags({
  publisherId
}: UseFeatureFlagsOptions = {}) {
  const axios = useAxios();
  const { portalId } = useParams();
  const { userDetails } = useUserDetails();

  const customHeaders: AxiosRequestHeaders | undefined = useMemo(() => {
    const headers: Record<string, string> = {};
    if (publisherId) {
      headers['x-publishers'] = publisherId;
    } else if (portalId) {
      headers['x-portal'] = portalId;
    }

    return headers as AxiosRequestHeaders;
  }, [publisherId, portalId]);

  const fetchFeatureFlags = useQuery({
    staleTime: 60 * 60 * 1000,
    refetchInterval: 60 * 60 * 1000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
    queryKey: [EQueryKeys.FEATURE_FLAGS, publisherId, portalId],
    queryFn: async () => {
      return await axios.get(
        getApiRouteOrMock(EApiRoutes.FEATURE_FLAGS),
        {},
        customHeaders
      );
    },
    select: (data) => data?.featureFlags ?? {},
    enabled: !!publisherId || !!portalId
  });

  const getCompanyFeatureFlags = useQuery({
    queryKey: [EQueryKeys.FEATURE_FLAGS, 'company'],
    queryFn: async () => {
      const response = await axios.get(
        getApiRouteOrMock(EApiRoutes.FEATURE_FLAGS) + '/company',
        {}
      );
      return response.featureFlags ?? {};
    },
    enabled: !!userDetails,
    staleTime: 60 * 60 * 1000,
    refetchInterval: 60 * 60 * 1000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false
  });

  const featureFlags = useMemo(
    () => fetchFeatureFlags.data ?? {},
    [fetchFeatureFlags.data]
  );
  const companyFeatureFlags = useMemo(
    () => getCompanyFeatureFlags.data ?? {},
    [getCompanyFeatureFlags.data]
  );

  const getFeatureFlagByKey = useCallback(
    (flagKey: EFeatureFlag, useCompanyFlags = false) => {
      const flags = useCompanyFlags ? companyFeatureFlags : featureFlags;
      return flags?.[flagKey];
    },
    [featureFlags, companyFeatureFlags]
  );

  return {
    featureFlags,
    isLoading: fetchFeatureFlags.isLoading,
    error: fetchFeatureFlags.error,
    companyFeatureFlags,
    companyIsLoading: getCompanyFeatureFlags.isLoading,
    companyError: getCompanyFeatureFlags.error,
    getFeatureFlagByKey
  };
}

